import { useMemo } from "react";
import { cn } from "@repo/utils";
import { useWorkflowsQuery } from "~/apis/use-workflows-query";
import { useMultipleWorkflowStatuses } from "~/pages/dashboard/task/hooks/apis/use-statuses";
import type { Item } from "~/types/api";
import { ItemLogsTable, LogTableColGroup, LogTableHeader } from "./item-log-table";
import { useItemStatusLogsBatch } from "../../../hooks/use-item-status-logs";

type Props = {
  items: Item[];
  isLoading?: boolean;
};

type ItemLogData = {
  taskId: number;
  itemId: number;
  itemName: string;
  workflowId: number;
};

export const ItemLogView = ({ items, isLoading = false }: Props) => {
  const itemsData = useMemo<ItemLogData[]>(() => {
    return items.map((item) => ({
      taskId: item.taskId,
      itemId: item.id,
      itemName: item.name,
      workflowId: item.workflowId,
    }));
  }, [items]);

  const { logsByItemId, isLoading: isLoadingLogs } = useItemStatusLogsBatch(itemsData);
  const { data: workflows, isLoading: isLoadingWorkflows } = useWorkflowsQuery();
  const workflowIds =
    workflows?.filter((w) => itemsData?.some((i) => i.workflowId === w.id)).map((w) => w.id) ??
    null;
  const { statuses, isLoading: isLoadingStatuses } = useMultipleWorkflowStatuses(
    workflowIds && workflowIds?.length > 0 ? workflowIds : [],
    workflowIds !== null
  );

  if (isLoading || isLoadingLogs || isLoadingWorkflows || isLoadingStatuses) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <span className="text-sm text-gray-500">Loading items...</span>
      </div>
    );
  }

  if (itemsData.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <span className="text-sm text-gray-500">No items selected</span>
      </div>
    );
  }
  const isSingle = itemsData.length === 1;

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col p-4", !isSingle && "overflow-y-auto")}>
      {itemsData.map((itemData, index) => (
        <div
          key={itemData.itemId}
          className={cn(
            "flex min-h-0 flex-col",
            isSingle ? "flex-1" : "mb-6 shrink-0",
            index > 0 && "border-t pt-4"
          )}
        >
          {!isSingle && (
            <h3 className="mb-3 text-sm font-semibold text-gray-800">{itemData.itemName}</h3>
          )}

          <div className={cn("min-h-0 overflow-auto", isSingle ? "flex-1" : "max-h-96")}>
            <table className="w-full min-w-max border-collapse">
              <LogTableColGroup />
              <LogTableHeader />
              <tbody>
                <ItemLogsTable
                  logs={logsByItemId[itemData.itemId] ?? []}
                  workflows={workflows}
                  statuses={statuses}
                />
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};
